/* Dr.Primal — capture step: the scratch card (the game).

   A canvas foil over real offer text. Drag to scratch it away; once REVEAL_THRESHOLD of
   it is clear the offer is revealed. No visible Reveal button (owner decision,
   2026-09-14), so the two WCAG routes live on the card itself: every single tap clears a
   patch where it lands (2.5.1: no drag needed), and the card is a focusable button that
   Enter or Space reveals (2.1.1). A browser that cannot paint the foil shows the offer
   and finishes on its own. All three paths end in the same `complete()`.

   A PLAIN card for now. Claude Design replaces the markup and the CSS; this contract
   stays: the factory takes the step root and ctx, and `mount()` resolves
   `{ revealed: 'scratch' | 'keyboard' | 'fallback' }`. Analytics belongs to the engine, so this file
   sends no events.

   - Pointer Events, so mouse, touch and pen behave the same. `touch-action: none` is on
     the canvas only (section CSS), so the page still scrolls.
   - The canvas is sized to devicePixelRatio when the step is shown, and scratching
     paints with `destination-out`.
   - The foil colour is read at runtime from `--dp-capture-foil` on the card, which the
     section sets to a token. This file contains no colour.
   - The cleared share is measured by drawing the foil into a SAMPLE_GRID × SAMPLE_GRID
     canvas and reading that: on pointerup, and at most every SAMPLE_INTERVAL_MS while
     dragging. Never a full-canvas read on each pointermove.
   - On reveal the offer's own Liquid-rendered text is copied into a polite status line,
     so it is announced. With reduced motion the foil goes at once, with no fade.
   - No user-facing English. */
(function () {
  'use strict';

  var REVEAL_THRESHOLD = 0.3;                   // share of the foil that must be cleared to count as revealed
  var SAMPLE_INTERVAL_MS = 150;                 // while dragging, the cleared share is measured at most this often
  var SAMPLE_GRID = 32;                         // the foil is measured on a GRID×GRID downscale; larger is slower and barely more exact
  var CLEAR_ALPHA = 128;                        // a sampled pixel below this alpha (0-255) counts as cleared
  var BRUSH_SHARE = 0.12;                       // brush radius as a share of the card's shorter side
  var REVEAL_HOLD_MS = 1200;                    // the revealed offer stays on screen this long before the email step
  var FOIL_PROPERTY = '--dp-capture-foil';      // the card's custom property holding the foil colour; set in sections/dp-capture.liquid
  var CLEARED_CLASS = 'dp-capture__foil--cleared'; // fades the foil out; its transition lives in the section CSS
  // Markup hooks. Each must match sections/dp-capture.liquid.
  var CARD_SELECTOR = '[data-dp-capture-card]';
  var FOIL_SELECTOR = '[data-dp-capture-foil]';
  var OFFER_SELECTOR = '[data-dp-capture-offer]';
  var ANNOUNCE_SELECTOR = '[data-dp-capture-announce]';
  var REVEAL_KEYS = ['Enter', ' '];              // keys that reveal the focused card; the card is role=button in the snippet

  var capture = window.dpCapture;
  if (!capture) {
    console.error('[dp-capture-step-scratch] dp-capture.js must load before this file');
    return;
  }

  /* The share of RGBA pixels whose alpha is below CLEAR_ALPHA. */
  function clearedShare(pixels) {
    var total = pixels.length / 4;
    if (!total) return 0;
    var cleared = 0;
    for (var i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < CLEAR_ALPHA) cleared += 1;
    }
    return cleared / total;
  }

  function dueForSample(now, lastSampleAt) {
    return now - lastSampleAt >= SAMPLE_INTERVAL_MS;
  }

  function isRevealed(share) {
    return share >= REVEAL_THRESHOLD;
  }

  function scratchStep(root, ctx) {
    var card = root.querySelector(CARD_SELECTOR);
    var foil = root.querySelector(FOIL_SELECTOR);
    var offer = root.querySelector(OFFER_SELECTOR);
    var announce = root.querySelector(ANNOUNCE_SELECTOR);

    var paint = null;
    var sampler = null;
    var drawing = false;
    var last = null;
    var lastSampleAt = 0;
    var revealed = false;
    var holdTimer = null;
    var finish = null;
    var listeners = [];

    function on(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push([target, type, handler]);
    }

    /* Paints the foil at the card's real size. False when there is nothing to paint
       with: no 2D canvas, or no colour on the card; the step then shows the offer and finishes. */
    function paintFoil() {
      var colour = window.getComputedStyle(card).getPropertyValue(FOIL_PROPERTY).trim();
      paint = foil.getContext ? foil.getContext('2d') : null;
      if (!paint || !colour) return false;

      var rect = card.getBoundingClientRect();
      var ratio = window.devicePixelRatio || 1;
      foil.width = Math.max(1, Math.round(rect.width * ratio));
      foil.height = Math.max(1, Math.round(rect.height * ratio));

      paint.fillStyle = colour;
      paint.fillRect(0, 0, foil.width, foil.height);
      paint.globalCompositeOperation = 'destination-out';
      paint.lineCap = 'round';
      paint.lineJoin = 'round';
      paint.lineWidth = 2 * BRUSH_SHARE * Math.min(foil.width, foil.height);
      return true;
    }

    // Pointer position in canvas pixels. Measured each time, so a resized dialog still
    // scratches under the finger.
    function point(event) {
      var rect = foil.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (foil.width / rect.width),
        y: (event.clientY - rect.top) * (foil.height / rect.height),
      };
    }

    function stroke(from, to) {
      paint.beginPath();
      paint.moveTo(from.x, from.y);
      paint.lineTo(to.x, to.y);
      paint.stroke();
    }

    function measure() {
      if (!sampler) {
        sampler = document.createElement('canvas');
        sampler.width = SAMPLE_GRID;
        sampler.height = SAMPLE_GRID;
      }
      var small = sampler.getContext('2d');
      small.clearRect(0, 0, SAMPLE_GRID, SAMPLE_GRID);
      small.drawImage(foil, 0, 0, SAMPLE_GRID, SAMPLE_GRID);
      return clearedShare(small.getImageData(0, 0, SAMPLE_GRID, SAMPLE_GRID).data);
    }

    function sample(now) {
      lastSampleAt = now;
      if (isRevealed(measure())) complete('scratch');
    }

    // The one way the step finishes: scratching, the keyboard, or the no-canvas fallback.
    function complete(how) {
      if (revealed) return;
      revealed = true;
      drawing = false;
      if (ctx.reducedMotion) foil.hidden = true;
      else foil.classList.add(CLEARED_CLASS);
      announce.textContent = offer.textContent.replace(/\s+/g, ' ').trim();
      holdTimer = window.setTimeout(function () {
        finish({ revealed: how });
      }, REVEAL_HOLD_MS);
    }

    function onDown(event) {
      if (revealed) return;
      drawing = true;
      if (foil.setPointerCapture) foil.setPointerCapture(event.pointerId);
      last = point(event);
      stroke(last, last);
    }

    function onMove(event) {
      if (!drawing) return;
      var next = point(event);
      stroke(last, next);
      last = next;
      var now = window.performance.now();
      if (dueForSample(now, lastSampleAt)) sample(now);
    }

    function onUp() {
      if (!drawing) return;
      drawing = false;
      last = null;
      sample(window.performance.now());
    }

    return {
      mount: function () {
        return new Promise(function (resolve) {
          finish = resolve;
          on(card, 'keydown', function (event) {
            if (REVEAL_KEYS.indexOf(event.key) === -1) return;
            if (event.preventDefault) event.preventDefault();
            complete('keyboard');
          });
          if (!paintFoil()) {
            foil.hidden = true;
            complete('fallback');
            return;
          }
          on(foil, 'pointerdown', onDown);
          on(foil, 'pointermove', onMove);
          on(foil, 'pointerup', onUp);
          on(foil, 'pointercancel', onUp);
        });
      },
      unmount: function () {
        window.clearTimeout(holdTimer);
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2]);
        });
        listeners = [];
        // Releases the backing store; a foil is painted once per page view.
        foil.width = 0;
        foil.height = 0;
      },
    };
  }

  // The pure parts, for tests/dp-capture/. The theme never calls these directly.
  scratchStep.core = { clearedShare: clearedShare, dueForSample: dueForSample, isRevealed: isRevealed };

  capture.registerStep('scratch', scratchStep, { game: true });
})();
